/* eslint-disable @typescript-eslint/no-unused-vars */
import {
	BrowserTracker,
	BuildType,
	Settings
} from "@scopely/web-sdk-analytics";
import { BrowserStorage } from "@scopely/web-sdk-core";
import {
	CacheService,
	DataSharingApiService,
	DataSharingConsentService
} from "@scopely/web-sdk-privacy-settings";
import {
	ScopelyIdentity,
	bootstrapScopelyIdentity
} from "@scopely/web-sdk-scopely-account";
import {
	OktaProviderConfig,
	Config as ScopelyIdConfig
} from "@scopely/web-sdk-scopelyid";
import {
	LOGIN_REDIRECT_URL,
	LOGOUT_REDIRECT_URL,
	SCOPELY_ANALYTICS_API_KEY,
	SCOPELY_IDENTITY_API_ENDPOINT,
	SCOPELY_IDENTITY_CLIENT_ID,
	SCOPELY_IDENTITY_PROVIDER_URL
} from "./constants";

// ScopelyId configuration
const provider = new OktaProviderConfig(
	SCOPELY_IDENTITY_PROVIDER_URL, // Auth server URL
	SCOPELY_IDENTITY_CLIENT_ID, // Okta client ID for V1
	LOGIN_REDIRECT_URL, // redirectUri
	LOGOUT_REDIRECT_URL, // logoutRedirectUri
	[] // Optional: social login providers
);

const config = new ScopelyIdConfig(provider, SCOPELY_IDENTITY_API_ENDPOINT);

let buildType = BuildType.Production;
if (process.env.REACT_APP_WITH_DEBUG === "true") {
	buildType = BuildType.Debug;
}
const settings = new Settings(SCOPELY_ANALYTICS_API_KEY, buildType);
export const analyticsTrackerV2 = new BrowserTracker(settings);
analyticsTrackerV2.register();

type PrivacySettingsTracker = ConstructorParameters<
	typeof DataSharingConsentService
>[0];

export const dataSharingService = new DataSharingConsentService(
	analyticsTrackerV2 as unknown as PrivacySettingsTracker,
	new CacheService(new BrowserStorage("privacy")),
	new DataSharingApiService(SCOPELY_ANALYTICS_API_KEY)
);

const USER_TRACKER_STORAGE_KEY = "store.solitairetripeaks.com:userTracker";
const AUTH_TOKEN_STORAGE_KEY = "store.solitairetripeaks.com:authToken";

const isValidPersistedAuthToken = (authToken: string | null) =>
	Boolean(authToken && authToken !== "null" && authToken !== "undefined");

const getPersistedAuthToken = () => {
	const authToken = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);

	if (!isValidPersistedAuthToken(authToken)) {
		return null;
	}

	try {
		const parsedAuthToken = JSON.parse(authToken) as
			| string
			| { value?: string }
			| null;

		if (typeof parsedAuthToken === "string") {
			return isValidPersistedAuthToken(parsedAuthToken)
				? parsedAuthToken
				: null;
		}

		const tokenValue = parsedAuthToken?.value ?? null;

		return isValidPersistedAuthToken(tokenValue) ? tokenValue : null;
	} catch {
		return authToken;
	}
};

export const getScopelyIdentityFallbackTenant = () =>
	process.env.REACT_APP_WITH_DEBUG === "true"
		? "web-v2-tenant-development"
		: "web-v2-tenant";

export const getScopelyIdentityTenant = () => {
	if (typeof localStorage === "undefined") {
		return getScopelyIdentityFallbackTenant();
	}

	const authToken = getPersistedAuthToken();
	const userTracker = localStorage.getItem(USER_TRACKER_STORAGE_KEY);

	if (authToken && userTracker) {
		try {
			const parsedUserTracker = JSON.parse(userTracker) as {
				id?: string;
			} | null;

			if (parsedUserTracker?.id) {
				return parsedUserTracker.id;
			}
		} catch (error) {
			console.error("Error parsing persisted userTracker:", error);
		}
	}

	return getScopelyIdentityFallbackTenant();
};

export const setScopelyIdentityTenant = (tenant: string) => {
	analyticsTrackerV2.getContext().setTenant(tenant);
};

export const createScopelyIdentity = (
	tenant?: string
): Promise<ScopelyIdentity> => {
	setScopelyIdentityTenant(tenant ?? getScopelyIdentityTenant());

	return bootstrapScopelyIdentity({
		config,
		tracker: analyticsTrackerV2
	});
};

// Service is initialized with V1 backend
//console.log("Identity service initialized");
